import React, { useEffect, useState, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import { useNavigate } from 'react-router-dom'

import { fmtMoney as fmtFull, fmtMoneyShort as fmt } from '../../utils/formatters'
import { getBizColor } from '../../utils/colors'
import { ARROW_R } from '../../utils/icons'
import { getTodayEastern, getDaysAgoEastern } from '../../lib/dateHelpers'

function useIsMobile() {
  var [mobile, setMobile] = useState(window.innerWidth < 768)
  useEffect(function() {
    var handle = function() { setMobile(window.innerWidth < 768) }
    window.addEventListener('resize', handle)
    return function() { window.removeEventListener('resize', handle) }
  }, [])
  return mobile
}

export default function OwnerDashboard() {
  var { profile, activeBusiness } = useAuth()
  var navigate = useNavigate()
  var isMobile = useIsMobile()
  var [loading, setLoading] = useState(true)
  var [todayJobs, setTodayJobs] = useState([])
    // eslint-disable-next-line no-unused-vars
    var [todayRoute, setTodayRoute] = useState(null)
    var [tomorrowRoute, setTomorrowRoute] = useState(null)
    // eslint-disable-next-line no-unused-vars
    var [loadingRoute, setLoadingRoute] = useState(false)
  var [upcomingJobs, setUpcomingJobs] = useState([])
  var [stats, setStats] = useState({
    activeJobs: 0, scheduledJobs: 0, completedThisMonth: 0,
    contractValue: 0, poolsValue: 0, riverbendValue: 0,
    pendingBids: 0, bidTotal: 0,
    overdueInvoices: 0, overdueAmount: 0, unpaidAmount: 0,
    weekExpenses: 0, weekHours: 0,
  })
  var [revenue, setRevenue] = useState({
    seasonTotal: 0, weekTotal: 0, count: 0, poolsTotal: 0, poolsCount: 0, riverbendTotal: 0, riverbendCount: 0,
  })
  var [recentBids, setRecentBids] = useState([])
  var [overdueInvs, setOverdueInvs] = useState([])
  // Mobile-specific state
  var [mobileUpcoming, setMobileUpcoming] = useState([])
  var [recentQuotes, setRecentQuotes] = useState([])
  var [weekStats, setWeekStats] = useState({ jobsScheduled: 0, quotesSent: 0, quotesSigned: 0 })
  var [monthStats, setMonthStats] = useState({ revenue: 0, bidsWon: 0, bidsLost: 0, conversionRate: 0 })

  var fetchRoute = async function(jobList) {
      var poolJobs = jobList.filter(function(j) { return j.business_id === 'pools' && j.site_address })
      if (poolJobs.length === 0) return null
      try {
        var resp = await fetch('/api/optimize-route', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ jobs: poolJobs.map(function(j) { return { id: j.id, title: j.title || j.client_name || 'Untitled', address: (j.site_address || '') + (j.site_city ? ', ' + j.site_city : '') + (j.site_state ? ', ' + j.site_state : ''), client_name: j.client_name, contract_value: j.contract_value, status: j.status } }) }) })
        var data = await resp.json()
        if (data.success) return data
      } catch (e) { console.error('Route error:', e) }
      return null
    }
    var fetchData = useCallback(async function() {
    setLoading(true)
    var today = getTodayEastern()
    var weekAgo = getDaysAgoEastern(7)
    var monthStart = today.slice(0, 7) + '-01'
    var next7 = getDaysAgoEastern(-7)

    // Today's jobs
    var jobQ = supabase.from('jobs').select('*').lte('start_date', today).gte('end_date', today).neq('status', 'cancelled')
    if (activeBusiness !== 'all') jobQ = jobQ.eq('business_id', activeBusiness)
    var { data: tj } = await jobQ
    // Also get jobs where end_date is null and start_date is today
    var jobQ2 = supabase.from('jobs').select('*').eq('start_date', today).is('end_date', null).neq('status', 'cancelled')
    if (activeBusiness !== 'all') jobQ2 = jobQ2.eq('business_id', activeBusiness)
    var { data: tj2 } = await jobQ2
    var allToday = [...(tj || []), ...(tj2 || [])]
    var seen = new Set()
    allToday = allToday.filter(function(j) { if (seen.has(j.id)) return false; seen.add(j.id); return true })
    setTodayJobs(allToday)

    // Upcoming 7 days
    var upQ = supabase.from('jobs').select('*').gt('start_date', today).lte('start_date', next7).neq('status', 'cancelled').order('start_date')
    if (activeBusiness !== 'all') upQ = upQ.eq('business_id', activeBusiness)
    var { data: uj } = await upQ
    setUpcomingJobs((uj || []).slice(0, 8))
      // Fetch tomorrow route
      var tomorrow = getDaysAgoEastern(-1)
      var tomorrowJobs = (uj || []).filter(function(j) { return j.start_date === tomorrow })
      fetchRoute(tomorrowJobs).then(function(r) { setTomorrowRoute(r) })

    // All active/scheduled jobs for stats
    var allQ = supabase.from('jobs').select('id, status, business_id, contract_value, start_date').in('status', ['active', 'scheduled']).neq('status', 'cancelled')
    if (activeBusiness !== 'all') allQ = allQ.eq('business_id', activeBusiness)
    var { data: allJobs } = await allQ

    var compQ = supabase.from('jobs').select('id').eq('status', 'completed').gte('start_date', monthStart)
    if (activeBusiness !== 'all') compQ = compQ.eq('business_id', activeBusiness)
    var { data: compJobs } = await compQ

    var activeJ = (allJobs || []).filter(function(j) { return j.status === 'active' })
    var schedJ = (allJobs || []).filter(function(j) { return j.status === 'scheduled' })
    var poolsVal = (allJobs || []).filter(function(j) { return j.business_id === 'pools' }).reduce(function(s, j) { return s + (parseFloat(j.contract_value) || 0) }, 0)
    var rbVal = (allJobs || []).filter(function(j) { return j.business_id === 'riverbend' }).reduce(function(s, j) { return s + (parseFloat(j.contract_value) || 0) }, 0)

    // Pending bids
    var bidQ = supabase.from('bids').select('id, total, status').in('status', ['draft', 'submitted'])
    if (activeBusiness !== 'all') bidQ = bidQ.eq('business_id', activeBusiness)
    var { data: bids } = await bidQ
    var bidTotal = (bids || []).reduce(function(s, b) { return s + (parseFloat(b.total) || 0) }, 0)

    // Recent bids
    var rbQ = supabase.from('bids').select('*').order('created_at', { ascending: false }).limit(5)
    if (activeBusiness !== 'all') rbQ = rbQ.eq('business_id', activeBusiness)
    var { data: rb } = await rbQ
    setRecentBids(rb || [])

    // Overdue invoices
    var invQ = supabase.from('invoices').select('*').in('status', ['sent', 'overdue']).lt('due_date', today)
    if (activeBusiness !== 'all') invQ = invQ.eq('business_id', activeBusiness)
    var { data: ovInvs } = await invQ
    var overdueAmt = (ovInvs || []).reduce(function(s, i) { return s + (parseFloat(i.balance_due) || 0) }, 0)
    setOverdueInvs((ovInvs || []).slice(0, 5))

    // Unpaid invoices total
    var unpaidQ = supabase.from('invoices').select('balance_due').in('status', ['draft', 'sent', 'overdue'])
    if (activeBusiness !== 'all') unpaidQ = unpaidQ.eq('business_id', activeBusiness)
    var { data: unpaid } = await unpaidQ
    var unpaidAmt = (unpaid || []).reduce(function(s, i) { return s + (parseFloat(i.balance_due) || 0) }, 0)

    // This week expenses
    var expQ = supabase.from('expenses').select('amount').gte('expense_date', weekAgo)
    if (activeBusiness !== 'all') expQ = expQ.eq('business_id', activeBusiness)
    var { data: exps } = await expQ
    var weekExp = (exps || []).reduce(function(s, e) { return s + (parseFloat(e.amount) || 0) }, 0)

    // This week hours
    var hrQ = supabase.from('time_entries').select('total_hours').gte('clock_in', new Date(Date.now() - 7 * 86400000).toISOString())
    if (activeBusiness !== 'all') hrQ = hrQ.eq('business_id', activeBusiness)
    var { data: hrs } = await hrQ
    var weekHrs = (hrs || []).reduce(function(s, h) { return s + (parseFloat(h.total_hours) || 0) }, 0)

    setStats({
      activeJobs: activeJ.length,
      scheduledJobs: schedJ.length,
      completedThisMonth: (compJobs || []).length,
      contractValue: poolsVal + rbVal,
      poolsValue: poolsVal,
      riverbendValue: rbVal,
      pendingBids: (bids || []).length,
      bidTotal: bidTotal,
      overdueInvoices: (ovInvs || []).length,
      overdueAmount: overdueAmt,
      unpaidAmount: unpaidAmt,
      weekExpenses: weekExp,
      weekHours: weekHrs,
    })

    // Revenue collected — completed jobs with amount_collected, this season (YTD)
    var seasonStartIso = new Date(new Date().getFullYear(), 0, 1).toISOString()
    var revQ = supabase.from('jobs').select('amount_collected, business_id, completed_at')
      .eq('status', 'completed').not('amount_collected', 'is', null)
      .gte('completed_at', seasonStartIso)
    if (activeBusiness !== 'all') revQ = revQ.eq('business_id', activeBusiness)
    var { data: revRows } = await revQ
    var weekAgoIso = new Date(Date.now() - 7 * 86400000).toISOString()
    var seasonTotal = 0, weekTotal = 0
    var poolsTotal = 0, poolsCount = 0
    var rbTotal = 0, rbCount = 0
    ;(revRows || []).forEach(function(r) {
      var amt = parseFloat(r.amount_collected) || 0
      seasonTotal += amt
      if (r.completed_at && r.completed_at >= weekAgoIso) weekTotal += amt
      if (r.business_id === 'pools') { poolsTotal += amt; poolsCount += 1 }
      else if (r.business_id === 'riverbend') { rbTotal += amt; rbCount += 1 }
    })
    setRevenue({
      seasonTotal: seasonTotal, weekTotal: weekTotal, count: (revRows || []).length,
      poolsTotal: poolsTotal, poolsCount: poolsCount,
      riverbendTotal: rbTotal, riverbendCount: rbCount,
    })

    // === Mobile-specific data ===
    // Upcoming 7 days (all jobs including today)
    var mUpQ = supabase.from('jobs').select('*, profiles!jobs_crew_leader_id_fkey(full_name)').gte('start_date', today).lte('start_date', next7).neq('status', 'cancelled').order('start_date')
    if (activeBusiness !== 'all') mUpQ = mUpQ.eq('business_id', activeBusiness)
    var { data: mUpJobs } = await mUpQ
    setMobileUpcoming(mUpJobs || [])

    // Recent quotes sent (last 14 days)
    var twoWeeksAgo = getDaysAgoEastern(14)
    var quotesQ = supabase.from('bids').select('*').in('quote_status', ['sent', 'viewed', 'signed', 'declined']).gte('quote_sent_at', twoWeeksAgo).order('quote_sent_at', { ascending: false })
    if (activeBusiness !== 'all') quotesQ = quotesQ.eq('business_id', activeBusiness)
    var { data: quotes } = await quotesQ
    setRecentQuotes(quotes || [])

    // Week stats
    var weekStartDate = new Date()
    weekStartDate.setDate(weekStartDate.getDate() - weekStartDate.getDay())
    var weekStartStr = weekStartDate.toISOString().split('T')[0]
    var weekJobsQ = supabase.from('jobs').select('id').gte('start_date', weekStartStr).lte('start_date', next7).neq('status', 'cancelled')
    if (activeBusiness !== 'all') weekJobsQ = weekJobsQ.eq('business_id', activeBusiness)
    var { data: weekJobsData } = await weekJobsQ
    var weekQuotesSentQ = supabase.from('bids').select('id, quote_status').gte('quote_sent_at', weekStartStr)
    if (activeBusiness !== 'all') weekQuotesSentQ = weekQuotesSentQ.eq('business_id', activeBusiness)
    var { data: weekQuotesData } = await weekQuotesSentQ
    var wqSigned = (weekQuotesData || []).filter(function(q) { return q.quote_status === 'signed' }).length
    setWeekStats({ jobsScheduled: (weekJobsData || []).length, quotesSent: (weekQuotesData || []).length, quotesSigned: wqSigned })

    // Month stats
    var monthBidsQ = supabase.from('bids').select('id, quote_status, total').gte('created_at', monthStart)
    if (activeBusiness !== 'all') monthBidsQ = monthBidsQ.eq('business_id', activeBusiness)
    var { data: monthBidsData } = await monthBidsQ
    var mWon = (monthBidsData || []).filter(function(b) { return b.quote_status === 'signed' })
    var mLost = (monthBidsData || []).filter(function(b) { return b.quote_status === 'declined' })
    var mRevenue = mWon.reduce(function(sum, b) { return sum + (parseFloat(b.total) || 0) }, 0)
    var mTotal = mWon.length + mLost.length
    setMonthStats({ revenue: mRevenue, bidsWon: mWon.length, bidsLost: mLost.length, conversionRate: mTotal > 0 ? Math.round((mWon.length / mTotal) * 100) : 0 })

    setLoading(false)
  }, [activeBusiness])

  useEffect(function() { fetchData() }, [fetchData])


  // === MOBILE DASHBOARD ===
  if (isMobile) {
    // Group upcoming jobs by date
    var groupByDate = function(jobs) {
      var groups = {}
      var today = getTodayEastern()
      var dates = []
      for (var d = 0; d < 7; d++) {
        dates.push(getDaysAgoEastern(-d))
      }
      dates.forEach(function(dateStr) { groups[dateStr] = [] })
      ;(jobs || []).forEach(function(j) { if (groups[j.start_date]) groups[j.start_date].push(j) })
      return { groups: groups, dates: dates, today: today }
    }
    var grouped = groupByDate(mobileUpcoming)

    var formatDateHeader = function(dateStr, todayStr) {
      var d = new Date(dateStr + 'T12:00:00')
      var dayName = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
      if (dateStr === todayStr) return 'Today \u2014 ' + dayName
      var tomorrow = getDaysAgoEastern(-1)
      if (dateStr === tomorrow) return 'Tomorrow \u2014 ' + dayName
      return dayName
    }

    var sevenDaysAgo = Date.now() - 7 * 86400000

    return (
      <div style={{ fontFamily: "'Barlow', sans-serif", paddingBottom: '100px' }}>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '22px', fontWeight: 700, letterSpacing: '2px', color: '#ffffff' }}>GOOD {getTimeOfDay()}, {profile?.full_name?.split(' ')[0]?.toUpperCase() || 'BOSS'}</div>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
        </div>

        {loading ? <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>Loading...</div> : (
          <div>
            {/* === UPCOMING JOBS (7 days) === */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '3px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>UPCOMING JOBS</div>
              {grouped.dates.map(function(dateStr) {
                var dayJobs = grouped.groups[dateStr]
                return (
                  <div key={dateStr} style={{ marginBottom: '12px' }}>
                    <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', color: dateStr === grouped.today ? '#b48c3c' : 'rgba(255,255,255,0.35)', marginBottom: '6px' }}>{formatDateHeader(dateStr, grouped.today)}</div>
                    {dayJobs.length === 0 ? (
                      <div style={{ padding: '10px 14px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: '4px', fontSize: '12px', color: 'rgba(255,255,255,0.15)' }}>No jobs scheduled</div>
                    ) : dayJobs.map(function(job) {
                      return (
                        <div key={job.id} style={{ padding: '12px 14px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', borderLeft: '3px solid ' + getBizColor(job.business_id), marginBottom: '4px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '14px', fontWeight: 600, color: '#ffffff' }}>{job.client_name || job.title || 'Untitled'}</div>
                              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' }}>{job.title && job.title !== job.client_name ? job.title : ''}</div>
                              {job.site_address && <a href={'https://maps.google.com/?q=' + encodeURIComponent(job.site_address)} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#4a6a8c', textDecoration: 'none', display: 'block', marginTop: '3px' }}>{job.site_address} \u2192</a>}
                              {job.pool_dimensions && <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', marginTop: '2px' }}>{job.pool_dimensions}{job.job_type ? ' \u2022 ' + job.job_type.replace(/_/g, ' ') : ''}</div>}
                              {job.profiles?.full_name && <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', marginTop: '2px' }}>Crew: {job.profiles.full_name}</div>}
                            </div>
                          </div>
                          {(job.client_phone || job.client_email) && (
                            <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                              {job.client_phone && <a href={'tel:' + job.client_phone} style={{ padding: '6px 12px', backgroundColor: 'rgba(74,140,106,0.12)', border: '1px solid rgba(74,140,106,0.25)', borderRadius: '3px', color: '#4a8c6a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1px', textDecoration: 'none' }}>CALL</a>}
                              {job.client_phone && <a href={'sms:' + job.client_phone} style={{ padding: '6px 12px', backgroundColor: 'rgba(74,106,140,0.12)', border: '1px solid rgba(74,106,140,0.25)', borderRadius: '3px', color: '#4a6a8c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1px', textDecoration: 'none' }}>TEXT</a>}
                              {job.site_address && <button onClick={function() { navigate('/calendar') }} style={{ padding: '6px 12px', backgroundColor: 'rgba(180,140,60,0.12)', border: '1px solid rgba(180,140,60,0.25)', borderRadius: '3px', color: '#b48c3c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1px', cursor: 'pointer' }}>SEND ETA</button>}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>

            {/* === RECENT QUOTES SENT (14 days) === */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '3px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>RECENT QUOTES SENT</div>
              {recentQuotes.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.15)', fontSize: '13px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '4px' }}>No quotes sent in the last 14 days.</div>
              ) : recentQuotes.map(function(q) {
                var qStatusColors = { sent: '#4a6a8c', viewed: '#b48c3c', signed: '#4a8c6a', declined: '#e05c5c' }
                var isViewed = q.quote_status === 'viewed'
                var sentDate = q.quote_sent_at ? new Date(q.quote_sent_at) : null
                var needsFollowUp = sentDate && (q.quote_status === 'sent' || q.quote_status === 'viewed') && sentDate.getTime() < sevenDaysAgo
                return (
                  <div key={q.id} onClick={function() { navigate('/bids') }} style={{ padding: '12px 14px', backgroundColor: isViewed ? 'rgba(180,140,60,0.04)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (isViewed ? 'rgba(180,140,60,0.15)' : 'rgba(255,255,255,0.06)'), borderRadius: '4px', marginBottom: '4px', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#ffffff' }}>{q.client_name || 'Unknown'}</div>
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' }}>{q.job_name || ''}</div>
                        {sentDate && <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)', marginTop: '2px' }}>Sent {sentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '16px', fontWeight: 700, color: '#b48c3c' }}>{fmtFull(q.total)}</div>
                        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1px', color: qStatusColors[q.quote_status] || '#888', marginTop: '4px' }}>{(q.quote_status || '').toUpperCase()}</div>
                      </div>
                    </div>
                    {needsFollowUp && <div style={{ marginTop: '6px', padding: '4px 8px', backgroundColor: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '3px', fontSize: '10px', fontWeight: 700, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '1px', color: '#e05c5c', display: 'inline-block' }}>NEEDS FOLLOW-UP</div>}
                    {isViewed && !needsFollowUp && <div style={{ marginTop: '6px', padding: '4px 8px', backgroundColor: 'rgba(180,140,60,0.1)', border: '1px solid rgba(180,140,60,0.2)', borderRadius: '3px', fontSize: '10px', fontWeight: 700, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '1px', color: '#b48c3c', display: 'inline-block' }}>HOT LEAD \u2014 VIEWED</div>}
                  </div>
                )
              })}
            </div>

            {/* === QUICK STATS === */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '3px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>QUICK STATS</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '14px 10px', textAlign: 'center' }}>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, color: '#ffffff', lineHeight: 1 }}>{weekStats.jobsScheduled}</div>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.3)', marginTop: '6px' }}>JOBS THIS WEEK</div>
                </div>
                <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '14px 10px', textAlign: 'center' }}>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, color: '#4a6a8c', lineHeight: 1 }}>{weekStats.quotesSent}</div>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.3)', marginTop: '6px' }}>QUOTES SENT</div>
                </div>
                <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '14px 10px', textAlign: 'center' }}>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, color: '#4a8c6a', lineHeight: 1 }}>{weekStats.quotesSigned}</div>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.3)', marginTop: '6px' }}>QUOTES SIGNED</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '14px 12px' }}>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '22px', fontWeight: 700, color: '#b48c3c', lineHeight: 1 }}>{fmt(monthStats.revenue)}</div>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.3)', marginTop: '6px' }}>REVENUE THIS MONTH</div>
                </div>
                <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '14px 12px' }}>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'baseline' }}>
                    <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '22px', fontWeight: 700, color: '#4a8c6a', lineHeight: 1 }}>{monthStats.bidsWon}W</span>
                    <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '16px', fontWeight: 700, color: '#e05c5c' }}>{monthStats.bidsLost}L</span>
                    <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '14px', fontWeight: 700, color: 'rgba(255,255,255,0.4)' }}>{monthStats.conversionRate}%</span>
                  </div>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.3)', marginTop: '6px' }}>BIDS WON / LOST / RATE</div>
                </div>
              </div>
            </div>

            {/* Quick Nav (mobile) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
              {[
                { label: 'CALENDAR', path: '/calendar', color: '#b48c3c' },
                { label: 'BIDS', path: '/bids', color: '#4a6a8c' },
                { label: 'INVOICING', path: '/invoicing', color: '#4a8c6a' },
              ].map(function(item) {
                return (
                  <button key={item.path} onClick={function() { navigate(item.path) }} style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid ' + item.color + '40', borderRadius: '4px', padding: '14px 8px', cursor: 'pointer', textAlign: 'center' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: item.color, margin: '0 auto 8px' }} />
                    <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', color: '#ffffff' }}>{item.label}</div>
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

  // === DESKTOP DASHBOARD ===
  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <div style={s.greeting}>GOOD {getTimeOfDay()}, {profile?.full_name?.split(' ')[0]?.toUpperCase() || 'BOSS'}</div>
          <div style={s.date}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</div>
        </div>
        <div style={s.badge}>OWNER</div>
      </div>

      {/* Revenue Collected — full-width hero card */}
      <div onClick={function() { navigate('/jobs') }} style={{ marginBottom: '20px', padding: '20px 24px', backgroundColor: 'rgba(74,140,106,0.08)', border: '1px solid rgba(74,140,106,0.3)', borderRadius: '6px', cursor: 'pointer' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '14px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2.5px', color: '#4a8c6a', marginBottom: '6px' }}>{String.fromCodePoint(0x1F4B0)} REVENUE COLLECTED \u2014 THIS SEASON</div>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '38px', fontWeight: 700, color: '#ffffff', lineHeight: 1 }}>{loading ? '-' : fmtFull(revenue.seasonTotal)}</div>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', marginTop: '6px' }}>
              {revenue.count} job{revenue.count !== 1 ? 's' : ''} completed
              {revenue.weekTotal > 0 && <span style={{ color: '#4a8c6a', marginLeft: '12px', fontWeight: 600 }}>+{fmtFull(revenue.weekTotal)} this week</span>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            {revenue.poolsTotal > 0 && <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', marginBottom: '4px' }}>Pools: <span style={{ color: '#b48c3c', fontWeight: 600 }}>{fmtFull(revenue.poolsTotal)}</span> ({revenue.poolsCount})</div>}
            {revenue.riverbendTotal > 0 && <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)' }}>Riverbend: <span style={{ color: '#4a8c6a', fontWeight: 600 }}>{fmtFull(revenue.riverbendTotal)}</span> ({revenue.riverbendCount})</div>}
          </div>
        </div>
      </div>

      {/* KPI Row */}
      <div style={s.kpiRow}>
        <div style={s.kpiCard} onClick={function() { navigate('/jobs') }}>
          <div style={s.kpiValue}>{loading ? '-' : stats.activeJobs}</div>
          <div style={s.kpiLabel}>ACTIVE JOBS</div>
        </div>
        <div style={s.kpiCard} onClick={function() { navigate('/calendar') }}>
          <div style={s.kpiValue}>{loading ? '-' : stats.scheduledJobs}</div>
          <div style={s.kpiLabel}>SCHEDULED</div>
        </div>
        <div style={s.kpiCard} onClick={function() { navigate('/bids') }}>
          <div style={{...s.kpiValue, color: '#4a6a8c'}}>{loading ? '-' : stats.pendingBids}</div>
          <div style={s.kpiLabel}>PENDING BIDS</div>
        </div>
        <div style={s.kpiCard} onClick={function() { navigate('/invoicing') }}>
          <div style={{...s.kpiValue, color: stats.overdueInvoices > 0 ? '#e05c5c' : '#4a8c6a'}}>{loading ? '-' : stats.overdueInvoices}</div>
          <div style={s.kpiLabel}>OVERDUE INVOICES</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiValue}>{loading ? '-' : stats.completedThisMonth}</div>
          <div style={s.kpiLabel}>COMPLETED THIS MONTH</div>
        </div>
      </div>

      {/* Money Row */}
      <div style={s.moneyRow}>
        <div style={s.moneyCard}>
          <div style={s.moneyLabel}>ACTIVE CONTRACT VALUE</div>
          <div style={s.moneyValue}>{loading ? '-' : fmt(stats.contractValue)}</div>
          <div style={s.moneyBreakdown}>
            <span style={{color: '#b48c3c'}}>Pools: {fmt(stats.poolsValue)}</span>
            <span style={{color: '#4a8c6a'}}>Riverbend: {fmt(stats.riverbendValue)}</span>
          </div>
        </div>
        <div style={s.moneyCard}>
          <div style={s.moneyLabel}>BID PIPELINE</div>
          <div style={{...s.moneyValue, color: '#4a6a8c'}}>{loading ? '-' : fmt(stats.bidTotal)}</div>
          <div style={s.moneySub}>{stats.pendingBids} pending bids</div>
        </div>
        <div style={s.moneyCard}>
          <div style={s.moneyLabel}>UNPAID INVOICES</div>
          <div style={{...s.moneyValue, color: stats.unpaidAmount > 0 ? '#e05c5c' : '#4a8c6a'}}>{loading ? '-' : fmt(stats.unpaidAmount)}</div>
          <div style={s.moneySub}>{stats.overdueInvoices > 0 ? stats.overdueInvoices + ' overdue (' + fmt(stats.overdueAmount) + ')' : 'All current'}</div>
        </div>
        <div style={s.moneyCard}>
          <div style={s.moneyLabel}>THIS WEEK</div>
          <div style={s.weekStats}>
            <div><span style={s.weekNum}>{loading ? '-' : Number(stats.weekHours).toFixed(0)}</span><span style={s.weekUnit}> hrs</span></div>
            <div><span style={s.weekNum}>{loading ? '-' : fmt(stats.weekExpenses)}</span><span style={s.weekUnit}> spent</span></div>
          </div>
        </div>
      </div>

      {/* Revenue Heatmap */}
        <div style={s.section}>
          <div style={s.sectionHeader}>
            <div style={s.sectionTitle}>REVENUE HEATMAP</div>
            <button onClick={function() { window.open('/api/revenue-heatmap?business=' + (activeBusiness || 'all'), '_blank') }} style={{...s.seeAllBtn, color: '#b48c3c'}}>{String.fromCodePoint(0x1F5FA)} OPEN MAP</button>
          </div>
          <div style={{...s.empty, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'12px', minHeight:'80px'}} onClick={function() { window.open('/api/revenue-heatmap?business=' + (activeBusiness || 'all'), '_blank') }}>
            <span style={{fontSize:'24px'}}>{String.fromCodePoint(0x1F5FA)}</span>
            <div><div style={{color:'rgba(255,255,255,0.5)', fontSize:'14px', fontWeight:600}}>View Revenue Heatmap</div><div style={{color:'rgba(255,255,255,0.2)', fontSize:'12px', marginTop:'2px'}}>See where your revenue comes from on a map</div></div>
          </div>
        </div>

        {/* Route Optimizer */}
        {todayRoute && todayRoute.route && todayRoute.route.length > 0 && <div style={s.section}>
          <div style={s.sectionHeader}><div style={s.sectionTitle}>TODAY'S ROUTE (POOLS)</div><div style={{fontFamily:"'Barlow Condensed', sans-serif",fontSize:'12px',color:'#4a8c6a',letterSpacing:'1px'}}>{todayRoute.total_time} {String.fromCodePoint(0x00B7)} {todayRoute.total_distance}</div></div>
          <div style={{...s.jobRow,borderLeft:'3px solid rgba(255,255,255,0.15)',background:'rgba(255,255,255,0.01)'}}><div style={s.jobMain}><div style={{...s.jobTitle,color:'rgba(255,255,255,0.4)',fontSize:'12px'}}>START: SHOP</div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.2)'}}>11260 Dunham Rd, Hartland</div></div></div>
          {todayRoute.route.map(function(stop) { return <div key={stop.id}><div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'8px',padding:'4px 0',color:'rgba(255,255,255,0.25)',fontSize:'10px'}}><div style={{flex:1,height:'1px',background:'rgba(255,255,255,0.06)'}}></div>{stop.drive_from_prev && <span>{String.fromCodePoint(0x1F697)} {stop.drive_from_prev.duration_text} ({stop.drive_from_prev.distance_text})</span>}<div style={{flex:1,height:'1px',background:'rgba(255,255,255,0.06)'}}></div></div><div style={{...s.jobRow,borderLeft:'3px solid #b48c3c'}} onClick={function(){navigate('/calendar')}}><div style={s.jobMain}><div style={s.jobTitle}><span style={{color:'#b48c3c',marginRight:'8px',fontSize:'11px'}}>STOP {stop.stop_number}</span>{stop.title}</div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.3)'}}>{String.fromCodePoint(0x1F4CD)} {stop.address}</div></div>{stop.contract_value && <div style={{fontFamily:"'Barlow Condensed', sans-serif",fontSize:'16px',fontWeight:700,color:'#b48c3c'}}></div>}</div></div> })}
        </div>}
        {tomorrowRoute && tomorrowRoute.route && tomorrowRoute.route.length > 0 && <div style={s.section}>
          <div style={s.sectionHeader}><div style={s.sectionTitle}>TOMORROW'S ROUTE (POOLS)</div><div style={{fontFamily:"'Barlow Condensed', sans-serif",fontSize:'12px',color:'#4a6a8c',letterSpacing:'1px'}}>{tomorrowRoute.total_time} {String.fromCodePoint(0x00B7)} {tomorrowRoute.total_distance}</div></div>
          <div style={{...s.jobRow,borderLeft:'3px solid rgba(255,255,255,0.15)',background:'rgba(255,255,255,0.01)'}}><div style={s.jobMain}><div style={{...s.jobTitle,color:'rgba(255,255,255,0.4)',fontSize:'12px'}}>START: SHOP</div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.2)'}}>11260 Dunham Rd, Hartland</div></div></div>
          {tomorrowRoute.route.map(function(stop) { return <div key={stop.id}><div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'8px',padding:'4px 0',color:'rgba(255,255,255,0.25)',fontSize:'10px'}}><div style={{flex:1,height:'1px',background:'rgba(255,255,255,0.06)'}}></div>{stop.drive_from_prev && <span>{String.fromCodePoint(0x1F697)} {stop.drive_from_prev.duration_text} ({stop.drive_from_prev.distance_text})</span>}<div style={{flex:1,height:'1px',background:'rgba(255,255,255,0.06)'}}></div></div><div style={{...s.jobRow,borderLeft:'3px solid #4a6a8c'}}><div style={s.jobMain}><div style={s.jobTitle}><span style={{color:'#4a6a8c',marginRight:'8px',fontSize:'11px'}}>STOP {stop.stop_number}</span>{stop.title}</div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.3)'}}>{String.fromCodePoint(0x1F4CD)} {stop.address}</div></div>{stop.contract_value && <div style={{fontFamily:"'Barlow Condensed', sans-serif",fontSize:'16px',fontWeight:700,color:'#b48c3c'}}></div>}</div></div> })}
        </div>}

        {/* Today's Jobs */}
      <div style={s.section}>
        <div style={s.sectionHeader}>
          <div style={s.sectionTitle}>TODAY'S JOBS</div>
          <div style={s.sectionCount}>{todayJobs.length}</div>
        </div>
        {loading ? <div style={s.empty}>Loading...</div> : todayJobs.length === 0 ? (
          <div style={s.empty}>No jobs scheduled for today.</div>
        ) : (
          <div style={s.jobList}>
            {todayJobs.map(function(job) {
              return (
                <div key={job.id} style={{...s.jobRow, borderLeft: '3px solid ' + getBizColor(job.business_id)}} onClick={function() { navigate('/calendar') }}>
                  <div style={s.jobMain}>
                    <div style={s.jobTitle}>{job.title || job.client_name || 'Untitled'}</div>
                    <div style={s.jobMeta}>
                      {job.site_address && <span>{job.site_address}</span>}
                      {job.contract_value && <span style={{color: '#b48c3c'}}>{fmtFull(job.contract_value)}</span>}
                    </div>
                  </div>
                  <div style={{...s.jobStatus, color: job.status === 'active' ? '#b48c3c' : '#4a6a8c'}}>{(job.status || '').toUpperCase()}</div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Two Column: Upcoming + Overdue */}
      <div style={s.twoCol}>
        {/* Upcoming Jobs */}
        <div style={s.section}>
          <div style={s.sectionHeader}>
            <div style={s.sectionTitle}>NEXT 7 DAYS</div>
            <button onClick={function() { navigate('/calendar') }} style={s.seeAllBtn}>VIEW CALENDAR {ARROW_R}</button>
          </div>
          {upcomingJobs.length === 0 ? (
            <div style={s.empty}>No upcoming jobs.</div>
          ) : (
            <div style={s.jobList}>
              {upcomingJobs.map(function(job) {
                return (
                  <div key={job.id} style={{...s.jobRowSm, borderLeft: '3px solid ' + getBizColor(job.business_id)}}>
                    <div style={s.jobDate}>{new Date(job.start_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                    <div style={s.jobTitleSm}>{job.title || job.client_name || 'Untitled'}</div>
                    {job.contract_value && <div style={s.jobAmt}>{fmtFull(job.contract_value)}</div>}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Overdue Invoices */}
        <div style={s.section}>
          <div style={s.sectionHeader}>
            <div style={{...s.sectionTitle, color: overdueInvs.length > 0 ? '#e05c5c' : 'rgba(255,255,255,0.5)'}}>OVERDUE INVOICES</div>
            <button onClick={function() { navigate('/invoicing') }} style={s.seeAllBtn}>ALL INVOICES {ARROW_R}</button>
          </div>
          {overdueInvs.length === 0 ? (
            <div style={s.empty}>No overdue invoices.</div>
          ) : (
            <div style={s.jobList}>
              {overdueInvs.map(function(inv) {
                return (
                  <div key={inv.id} style={{...s.jobRowSm, borderLeft: '3px solid #e05c5c'}} onClick={function() { navigate('/invoicing') }}>
                    <div style={s.jobDate}>{inv.due_date ? new Date(inv.due_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '--'}</div>
                    <div style={s.jobTitleSm}>{inv.job_name || inv.client_name || 'Untitled'}</div>
                    <div style={{...s.jobAmt, color: '#e05c5c'}}>{fmtFull(inv.balance_due)}</div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Recent Bids */}
      <div style={s.section}>
        <div style={s.sectionHeader}>
          <div style={s.sectionTitle}>RECENT BIDS</div>
          <button onClick={function() { navigate('/bids') }} style={s.seeAllBtn}>ALL BIDS {ARROW_R}</button>
        </div>
        {recentBids.length === 0 ? (
          <div style={s.empty}>No bids yet.</div>
        ) : (
          <div style={s.jobList}>
            {recentBids.map(function(bid) {
              var statusColors = { draft: '#b48c3c', submitted: '#4a6a8c', awarded: '#4a8c6a', lost: '#e05c5c', completed: 'rgba(255,255,255,0.3)' }
              return (
                <div key={bid.id} style={{...s.jobRowSm, borderLeft: '3px solid ' + getBizColor(bid.business_id)}} onClick={function() { navigate('/bids') }}>
                  <div style={s.jobTitleSm}>{bid.job_name || bid.client_name || 'Untitled'}</div>
                  <div style={{...s.jobStatus, color: statusColors[bid.status] || '#b48c3c'}}>{(bid.status || '').toUpperCase()}</div>
                  <div style={s.jobAmt}>{fmtFull(bid.total)}</div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Quick Nav */}
      <div style={s.quickRow}>
        {[
          { label: 'CALENDAR', path: '/calendar', color: '#b48c3c' },
          { label: 'BIDS', path: '/bids', color: '#4a6a8c' },
          { label: 'INVOICING', path: '/invoicing', color: '#4a8c6a' },
          { label: 'TIME CLOCK', path: '/timeclock', color: '#8c4a8c' },
          { label: 'EXPENSES', path: '/expenses', color: '#8c7a4a' },
          { label: 'TEAM', path: '/team', color: '#4a8c6a' },
        ].map(function(item) {
          return (
            <button key={item.path} onClick={function() { navigate(item.path) }} style={{...s.quickCard, borderColor: item.color + '40'}}>
              <div style={{...s.quickDot, backgroundColor: item.color}} />
              <div style={s.quickLabel}>{item.label}</div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function getTimeOfDay() {
  var h = new Date().getHours()
  if (h < 12) return 'MORNING'
  if (h < 17) return 'AFTERNOON'
  return 'EVENING'
}

var s = {
  container: { maxWidth: '1100px', fontFamily: "'Barlow', sans-serif" },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px' },
  greeting: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  date: { fontSize: '15px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' },
  badge: { backgroundColor: 'rgba(180,140,60,0.15)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', padding: '6px 12px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', letterSpacing: '2px', color: '#b48c3c' },

  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '20px' },
  kpiCard: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '20px', textAlign: 'center', cursor: 'pointer' },
  kpiValue: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '36px', fontWeight: 700, color: '#ffffff', lineHeight: 1 },
  kpiLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginTop: '8px' },

  moneyRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '28px' },
  moneyCard: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '20px' },
  moneyLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '8px' },
  moneyValue: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, color: '#b48c3c', lineHeight: 1, marginBottom: '8px' },
  moneyBreakdown: { display: 'flex', gap: '16px', fontSize: '12px' },
  moneySub: { fontSize: '12px', color: 'rgba(255,255,255,0.35)' },
  weekStats: { display: 'flex', flexDirection: 'column', gap: '4px' },
  weekNum: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '22px', fontWeight: 700, color: '#ffffff' },
  weekUnit: { fontSize: '12px', color: 'rgba(255,255,255,0.35)' },

  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '8px' },

  section: { marginBottom: '24px' },
  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  sectionTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '3px', color: 'rgba(255,255,255,0.5)' },
  sectionCount: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '14px', fontWeight: 700, color: '#b48c3c' },
  seeAllBtn: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '1.5px', cursor: 'pointer' },

  empty: { padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.2)', fontSize: '14px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.05)' },

  jobList: { display: 'flex', flexDirection: 'column', gap: '4px' },
  jobRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '14px 16px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px', cursor: 'pointer' },
  jobRowSm: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '3px', cursor: 'pointer' },
  jobMain: { flex: 1 },
  jobTitle: { fontSize: '15px', fontWeight: 600, color: '#ffffff' },
  jobTitleSm: { fontSize: '13px', fontWeight: 500, color: '#ffffff', flex: 1 },
  jobMeta: { display: 'flex', gap: '16px', fontSize: '12px', color: 'rgba(255,255,255,0.35)', marginTop: '3px' },
  jobDate: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '1px', color: 'rgba(255,255,255,0.35)', minWidth: '80px' },
  jobAmt: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '14px', fontWeight: 600, color: '#b48c3c', flexShrink: 0 },
  jobStatus: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '1.5px', flexShrink: 0 },

  quickRow: { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px', marginTop: '8px' },
  quickCard: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid', borderRadius: '4px', padding: '16px', cursor: 'pointer', textAlign: 'center' },
  quickDot: { width: '6px', height: '6px', borderRadius: '50%', margin: '0 auto 10px' },
  quickLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', color: '#ffffff' },
}
